import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { AudioFile, getUserFiles } from '../services/api';

interface UserFilesContextType {
  files: AudioFile[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  removeFile: (id: string) => void;
}

const UserFilesContext = createContext<UserFilesContextType | undefined>(undefined);

export const useUserFiles = (): UserFilesContextType => {
  const ctx = useContext(UserFilesContext);
  if (!ctx) {
    throw new Error('useUserFiles must be used within a UserFilesProvider');
  }
  return ctx;
};

interface UserFilesProviderProps {
  children: ReactNode;
}

export const UserFilesProvider: React.FC<UserFilesProviderProps> = ({ children }) => {
  const { isAuthenticated, user, getValidToken } = useAuth();
  const [files, setFiles] = useState<AudioFile[]>([]);
  // Initialize loading=true so consumers never see a flash of empty state
  // before the first fetch starts.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dedup concurrent calls — StrictMode double-invoke, churn from auth callbacks,
  // and concurrent refresh() from multiple consumers all collapse to one fetch.
  const inFlight = useRef<Promise<AudioFile[]> | null>(null);
  // Track which user we have data for, so a sign-out/sign-in resets state.
  const loadedFor = useRef<string | null>(null);
  const hasLoaded = useRef(false);

  const fetchFiles = useCallback(async (): Promise<AudioFile[]> => {
    // Throw rather than returning [] — empty fallback would silently overwrite
    // a populated cache when the caller blindly does setFiles(result).
    if (!isAuthenticated || !user?.sub) {
      throw new Error('Not authenticated');
    }
    if (inFlight.current) {
      return inFlight.current;
    }
    const promise = (async () => {
      const token = await getValidToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      const result = await getUserFiles(user.sub, token);
      return result;
    })();
    inFlight.current = promise;
    try {
      const result = await promise;
      return result;
    } finally {
      inFlight.current = null;
    }
  }, [isAuthenticated, user, getValidToken]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !user?.sub) {
      // Don't clear cached files on transient auth gaps. A real sign-out
      // happens via signOut() which redirects, so files going stale here
      // can only mean a token-refresh flicker — keep the cache.
      setLoading(false);
      loadedFor.current = null;
      hasLoaded.current = false;
      return;
    }
    try {
      setError(null);
      if (!hasLoaded.current) {
        setLoading(true);
      }
      const result = await fetchFiles();
      setFiles(result);
      loadedFor.current = user.sub;
      hasLoaded.current = true;
    } catch (err) {
      console.error('UserFilesContext: failed to fetch', err);
      setError('Failed to load files');
      // Intentionally do NOT clear files — preserve last-known-good cache.
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchFiles]);

  // Initial fetch + reset on user change.
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) {
      // Auth gap — don't wipe cache. Just mark "needs reload on next valid auth".
      setLoading(false);
      loadedFor.current = null;
      hasLoaded.current = false;
      return;
    }
    if (loadedFor.current === user.sub) {
      return;
    }
    refresh();
  }, [isAuthenticated, user?.sub, refresh]);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const value: UserFilesContextType = {
    files,
    loading,
    error,
    refresh,
    removeFile,
  };

  return <UserFilesContext.Provider value={value}>{children}</UserFilesContext.Provider>;
};
